import { createFileRoute, redirect } from "@tanstack/react-router";
import { userBuildingsResolverOptions } from "@/api";
import { waitForAuthReady } from "@/lib";
import { buildingScopedKey, queryClient } from "@/lib/queryClient";
import BuildingContactPage from "@/components/building-contact/BuildingContactPage";
import ContactLoadError, {
  ContactLoadPending,
} from "@/components/building-contact/ContactLoadError";
import { resolveContactBuildingTarget } from "@/components/building-contact/resolveContactBuilding";

// Printed on QR signs (FUN-1338), so the building comes from the path and the
// URL never changes. Not under /building, which the sidebar treats as the
// building admin workspace.
export const Route = createFileRoute("/_portal/contact/$buildingId")({
  // BuildingUserProvider re-runs beforeLoad, rather than sending the user to
  // the dashboard, when it finds no valid `?building=`.
  staticData: { resolvesOwnBuilding: true },
  // The app reads the building from `?building=`, and BuildingUserProvider
  // bounces any value the user cannot open. Point it at the path's building
  // when the user belongs there, else at their default, so the page can show
  // "no access" instead of being bounced to the dashboard.
  beforeLoad: async ({ params, search }) => {
    const { buildingId } = params;
    const current = search.building;
    const user = await waitForAuthReady();

    // Logged out: AuthProvider shows the login at this same URL and reloads on
    // success, so the user lands back here.
    if (!user) {
      if (current !== buildingId) {
        throw redirect({
          to: "/contact/$buildingId",
          params,
          search: (prev) => ({ ...prev, building: buildingId }),
          replace: true,
        });
      }
      return;
    }

    // Through the shared client: the app's retries apply. A persistent failure
    // raises out of this route. With no valid `?building=` yet, that also
    // leaves BuildingUserProvider without a building, so it shows its error
    // screen and its retry re-runs this route. Once `?building=` is set — any
    // reload, and the return from login — the provider has one and stays
    // quiet, so `errorComponent` owns the retry. See ContactLoadError.
    const resolver = userBuildingsResolverOptions(user.uid);
    const buildings = await queryClient.fetchQuery({
      ...resolver,
      queryKey: buildingScopedKey(current, resolver.queryKey),
    });
    const target = resolveContactBuildingTarget(
      buildingId,
      buildings.map((b) => b.buildingId),
    );
    // No buildings at all: let BuildingUserProvider show its own screen.
    if (!target) return;

    if (current !== target) {
      // Seed the entry the provider and the page read once `?building=` is
      // `target`, so the list is fetched once per scan.
      queryClient.setQueryData(
        buildingScopedKey(target, resolver.queryKey),
        buildings,
      );
      throw redirect({
        to: "/contact/$buildingId",
        params,
        search: (prev) => ({ ...prev, building: target }),
        replace: true,
      });
    }
  },
  component: RouteComponent,
  // A failed membership lookup is the one error this route raises, and it is
  // recoverable: offer a retry here rather than falling through to the
  // app-wide boundary. See ContactLoadError for when the provider's own
  // error screen does not cover it.
  errorComponent: ContactLoadError,
  pendingComponent: ContactLoadPending,
});

function RouteComponent() {
  const { buildingId } = Route.useParams();
  return <BuildingContactPage buildingId={buildingId} />;
}
